/**
 * Real `MandateAPI` implementation backed by the HTTP client (ADR-0020).
 *
 * Closes the lone mock import the ADR-0019 swap left behind. The
 * dashboard's `mandates` bag has no dedicated server route in Phase 1
 * (the conceptual surface is "Discovery records", which the server
 * exposes through `/v1/audit` with a `decision_class=discovery`
 * filter). This bag composes those endpoints with a client-side
 * Discovery filter so the `MandateAPI` shape stays unchanged for the
 * views.
 *
 * Wire mapping:
 *   - listObserved(site_id, q?) →
 *       GET /v1/audit?decision_class=discovery&site_id=…
 *       Returns `{ items: AuditRecord[], next_cursor: string | null }`.
 *       The bag re-filters to `decision === 'observed'` (defense-in-
 *       depth against any future server bug that lets a non-Discovery
 *       row through the chain_class filter), then applies the
 *       `AuditQuery` `q` / `filter` pills client-side via
 *       `filterRecords` (same helper the mock uses).
 *
 *   - get(record_id) → GET /v1/audit/:record_id
 *       Returns the unwrapped record on 200. Returns `null` on 404
 *       (matches the mock's null-on-miss shape; ADR-0019 §4.3 — RLS
 *       denials also manifest as 404 so the dashboard's response is
 *       identical for "wrong account" vs "truly missing"). Returns
 *       `null` if the record's decision is not `'observed'` (the
 *       single-record endpoint has no decision filter; the dashboard
 *       must NOT surface Transactional records under the Mandates
 *       view).
 *
 * Page size: the server's list response is bounded to 50 rows per
 * page (ADR-0010 §3.4). ADR-0020 §1 / §3.4 — the dashboard does not
 * paginate; `next_cursor` is ignored. A future ADR adds the "load
 * more" UI + cursor plumbing when merchant feedback warrants it.
 */

import type { AuditRecord, AuditRecordId, SiteId } from '@sill/types';
import type {
  AuditQuery,
  MandateAPI,
  MandateListPage,
  UcpSessionDetailResponse,
  UcpSessionsListResponse,
  VisitGroupPage,
} from '@sill/types/api';

import { filterRecords } from '../lib/filter-records.js';
import type { MandateRecord } from '../views/mandates/types.js';
import { ApiError, type HttpClient } from './http-client.js';

export interface RealMandatesDeps {
  readonly http: HttpClient;
}

/**
 * Wire row from `GET /v1/audit` list/cursor/page. Carries `settlement`
 * (the dashboard-local `MandateRecord` extension) because the server
 * projects `discovery_context.settlement` onto BOTH list endpoints via
 * its `extractAllowedSettlement` allow-list extractor — the row card's
 * headline needs `settlement.total_minor` to render the ACTUAL settled
 * total instead of the agent-signed `intent.max_amount` cap (ADR-0063 /
 * ADR-0066). Typing it as `MandateRecord` (rather than the bare
 * `AuditRecord`) makes that contract explicit so a future map/reshape of
 * the list rows can't silently drop the field and re-introduce the
 * "$50 cap on the card vs $21.71 total in the detail" discrepancy.
 */
interface AuditListResponse {
  items: MandateRecord[];
  next_cursor: string | null;
  total_count?: number;
  page?: number;
  total_pages?: number;
}

/**
 * Single-record response — the wire shape adds a `payload?` field
 * carrying the inspectable request/response canonical-JSON content
 * (ADR-0059 §6). `AuditRecord` itself does NOT carry it; payload
 * rides on the wire next to `record`.
 */
interface AuditRecordResponse {
  record: AuditRecord & {
    payload?: import('@sill/types').AuditRecordPayload;
  };
}

/**
 * Visits panel Step B — the filters the grouped list and a group's records
 * share with `GET /v1/audit` (same names, same server semantics).
 */
/** `tz` rides along only with a date bound, and only when not UTC. */
function setTzParam(params: URLSearchParams, q?: AuditQuery): void {
  const dated = (q?.date_from ?? '').length > 0 || (q?.date_to ?? '').length > 0;
  if (dated && q?.tz !== undefined && q.tz.length > 0 && q.tz !== 'UTC') params.set('tz', q.tz);
}

function groupFilterParams(q?: AuditQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (q?.q !== undefined && q.q.trim().length > 0) params.set('q', q.q.trim());
  if (q?.identity !== undefined && q.identity !== 'all') params.set('identity', q.identity);
  if (q?.status !== undefined && q.status !== 'all') params.set('status', q.status);
  if (q?.segment !== undefined) params.set('segment', q.segment);
  if (q?.date_from !== undefined && q.date_from.length > 0) params.set('date_from', q.date_from);
  if (q?.date_to !== undefined && q.date_to.length > 0) params.set('date_to', q.date_to);
  setTzParam(params, q);
  if (q?.page !== undefined && q.page >= 1) params.set('page', String(q.page));
  // ADR-0253 §A — protocol filter (groups + per-group records honour it too).
  if (q?.protocol !== undefined && q.protocol !== 'all') params.set('protocol', q.protocol);
  return params;
}

export function createRealMandatesApi(deps: RealMandatesDeps): MandateAPI {
  return {
    async listObserved(site_id: SiteId, q?: AuditQuery): Promise<MandateListPage> {
      // ADR-0022 — push `q`, `filter`, and `cursor` to the server so
      // the WHERE acts on the full dataset, not just the loaded page.
      //
      // Phase 2 (2026-05-31): the `decision_class=discovery` server
      // filter has been dropped so the view surfaces Phase 2
      // transactional records alongside the Phase 1 discovery records.
      // The dashboard's `filterRecords` helper still runs as
      // defense-in-depth for the search box + filter pills.
      const params = new URLSearchParams();
      params.set('site_id', site_id);
      if (q?.q !== undefined && q.q.trim().length > 0) {
        params.set('q', q.q.trim());
      }
      // Identity segment (`?identity=`, labels-v2 Step A). The deprecated
      // `filter` → `verification=` alias (Sill's own record signature,
      // not the visitor) is no longer sent by the dashboard.
      if (q?.identity !== undefined && q.identity !== 'all') {
        params.set('identity', q.identity);
      }
      if (q?.status !== undefined && q.status !== 'all') {
        params.set('status', q.status);
      }
      // Visits panel Step C — the list's filter chip (`?segment=`).
      if (q?.segment !== undefined) {
        params.set('segment', q.segment);
      }
      if (q?.date_from !== undefined && q.date_from.length > 0) {
        params.set('date_from', q.date_from);
      }
      if (q?.date_to !== undefined && q.date_to.length > 0) {
        params.set('date_to', q.date_to);
      }
      setTzParam(params, q);
      // Settled-only filter — wired to `?settled=true` only when the
      // caller opts in. Omitted otherwise (server treats absence as the
      // unchanged-behavior path). Powers the dashboard's Signed
      // Transactions panel — restricts the list to
      // `discovery_context.settlement.outcome = 'succeeded'` so the panel
      // is not starved by the recent-50 cap.
      if (q?.settled === true) {
        params.set('settled', 'true');
      }
      if (q?.agent_key !== undefined && q.agent_key.length > 0) {
        params.set('agent_key', q.agent_key);
      }
      // ADR-0253 §A — protocol filter chip.
      if (q?.protocol !== undefined && q.protocol !== 'all') {
        params.set('protocol', q.protocol);
      }
      // Numbered (offset) pagination takes precedence over the cursor
      // stream. The Mandates view drives `page`; `cursor` stays plumbed
      // for any non-paged consumer.
      if (q?.page !== undefined && q.page >= 1) {
        params.set('page', String(q.page));
      } else if (q?.cursor !== undefined && q.cursor.length > 0) {
        params.set('cursor', q.cursor);
      }

      const res = await deps.http.request<AuditListResponse>({
        method: 'GET',
        path: `/v1/audit?${params.toString()}`,
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      // Defense-in-depth: apply the search box + filter pills locally.
      // Server filters already; client repeats the work to catch any
      // server-side regression. The previous `decision === 'observed'`
      // invariant was removed in Phase 2 (2026-05-31) so transactional
      // records surface alongside discovery.
      //
      // NOTE: `total_count` is the server's pre-filter count for the
      // page. The client-side `filterRecords` pass is defense-in-depth
      // and, in normal operation, a no-op (the server already applied
      // the same predicates), so the count stays consistent with the
      // rendered rows. If a server regression ever drops rows here, the
      // count would over-report for that page only.
      // The search text is the server's alone (founder 2026-10-19): it
      // matches what a record does not carry (an IP's visits, the
      // "Unknown scanner" label, page-hit search terms), so a local
      // re-match would drop real results.
      const { q: _search, ...local } = q ?? {};
      const page: MandateListPage = {
        records: filterRecords(res.items, local),
        next_cursor: res.next_cursor,
      };
      if (res.total_count !== undefined) page.total_count = res.total_count;
      if (res.page !== undefined) page.page = res.page;
      if (res.total_pages !== undefined) page.total_pages = res.total_pages;
      return page;
    },

    async get(id: AuditRecordId): Promise<AuditRecord | null> {
      try {
        const res = await deps.http.request<AuditRecordResponse>({
          method: 'GET',
          path: `/v1/audit/${encodeURIComponent(id)}`,
        });
        if (res === undefined) {
          throw new ApiError('bad_response', null);
        }
        return res.record;
      } catch (err) {
        if (err instanceof ApiError && err.kind === 'not_found') {
          return null;
        }
        throw err;
      }
    },

    async listByAgent(site_id: SiteId, agent_id: string): Promise<MandateListPage> {
      const params = new URLSearchParams();
      params.set('site_id', site_id);
      params.set('matched_agent_id', agent_id);
      const res = await deps.http.request<AuditListResponse>({
        method: 'GET',
        path: `/v1/audit?${params.toString()}`,
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      const page: MandateListPage = {
        records: res.items,
        next_cursor: res.next_cursor,
      };
      if (res.total_count !== undefined) page.total_count = res.total_count;
      return page;
    },

    async listAgentGroups(site_id: SiteId, q?: AuditQuery): Promise<VisitGroupPage> {
      const res = await deps.http.request<VisitGroupPage>({
        method: 'GET',
        path: `/v1/sites/${encodeURIComponent(site_id)}/visit-groups?${groupFilterParams(q).toString()}`,
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      return res;
    },

    async listGroupRecords(site_id: SiteId, group_key: string, q?: AuditQuery): Promise<MandateListPage> {
      const res = await deps.http.request<AuditListResponse>({
        method: 'GET',
        path: `/v1/sites/${encodeURIComponent(site_id)}/visit-groups/${encodeURIComponent(group_key)}/records?${groupFilterParams(q).toString()}`,
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      const page: MandateListPage = { records: res.items, next_cursor: null };
      if (res.total_count !== undefined) page.total_count = res.total_count;
      if (res.page !== undefined) page.page = res.page;
      if (res.total_pages !== undefined) page.total_pages = res.total_pages;
      return page;
    },

    // Founder 2026-10-23 — UCP checkout lifecycle view (unsigned).
    async listUcpSessions(site_id: SiteId, page = 1): Promise<UcpSessionsListResponse> {
      const res = await deps.http.request<UcpSessionsListResponse>({
        method: 'GET',
        path: `/v1/sites/${encodeURIComponent(site_id)}/ucp-sessions?page=${page}`,
      });
      if (res === undefined) throw new ApiError('bad_response', null);
      return res;
    },

    async getUcpSession(site_id: SiteId, session_id: string): Promise<UcpSessionDetailResponse | null> {
      try {
        const res = await deps.http.request<UcpSessionDetailResponse>({
          method: 'GET',
          path: `/v1/sites/${encodeURIComponent(site_id)}/ucp-sessions/${encodeURIComponent(session_id)}`,
        });
        if (res === undefined) throw new ApiError('bad_response', null);
        return res;
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
  };
}
